/**
 * Button styling tokens, deliberately kept out of the `"use client"` Button
 * module so server components can style links and submit buttons identically.
 * Importing the class builder from a client module throws at render time
 * ("Attempted to call buttonClassName() from the server").
 */

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger' | 'ai' | 'link' | 'ghost' | 'chip' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export const buttonBaseClasses = 'inline-flex min-h-12 items-center justify-center gap-2 rounded-control border font-semibold transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ss-canvas)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 md:min-h-10';

export const buttonSizeClasses: Record<ButtonSize, string> = {
  sm: 'min-h-12 px-3 py-2 text-xs md:min-h-9',
  md: 'px-4 py-2.5 text-sm md:py-2',
  lg: 'min-h-12 px-5 py-3 text-base md:min-h-10',
  icon: 'h-12 w-12 min-w-12 p-0 md:h-10 md:min-w-10',
};

export const buttonVariantClasses: Record<ButtonVariant, string> = {
  primary: 'border-[var(--ss-brand)] bg-[var(--ss-brand)] text-[var(--ss-brand-contrast)] hover:border-[var(--ss-brand-strong)] hover:bg-[var(--ss-brand-strong)]',
  secondary: 'border-[var(--ss-border)] bg-[var(--ss-surface)] text-[var(--ss-text)] hover:bg-[var(--ss-surface-subtle)]',
  quiet: 'border-transparent bg-transparent text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)]',
  danger: 'border-[var(--ss-critical)] bg-[var(--ss-critical)] text-white hover:brightness-95',
  ai: 'border-[var(--ss-ai)] bg-[var(--ss-ai)] text-[var(--ss-ai-contrast)] hover:border-[var(--ss-ai-strong)] hover:bg-[var(--ss-ai-strong)]',
  link: 'min-h-0 border-transparent bg-transparent p-0 text-[var(--ss-focus)] hover:underline underline-offset-4',
  ghost: 'border-transparent bg-transparent text-[var(--ss-positive)] hover:bg-[var(--ss-positive-bg)]',
  chip: 'min-h-9 rounded-full border-transparent bg-[var(--ss-positive-bg)] px-3 text-[var(--ss-positive)] hover:brightness-95',
  subtle: 'min-h-9 border-transparent bg-transparent px-2 text-[var(--ss-text)] hover:bg-[var(--ss-surface-subtle)]',
};

/**
 * The exact classes `Button` applies, for elements that must be a link or a
 * submit control but should be visually identical to a button. Without this,
 * call sites hand-roll approximations and the primary style drifts per route.
 */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  className = '',
}: { variant?: ButtonVariant; size?: ButtonSize; className?: string } = {}) {
  return `${buttonBaseClasses} ${buttonSizeClasses[size]} ${buttonVariantClasses[variant]} ${className}`;
}
